import { Component, computed, inject, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import { MatRadioModule } from '@angular/material/radio';
import { TranslateModule } from '@ngx-translate/core';
import { DialogShellComponent } from '@custom-components';
import { FilledButtonComponent, TextButtonComponent } from '@custom-components/general/button';
import { TableComponent } from '@custom-components/general/table/table.component';
import { TableFilterBarComponent } from '@custom-components/general/table-filter-bar';
import { TagComponent } from '@custom-components/general/tag/tag.component';
import { InstallationStatusEnum } from '@core/models/device.model';
import { Installation } from '@core/models/installation.model';
import { DeviceStore } from '@root/app/store/device.store';

export type LinkTarget = Pick<Installation, 'installationOid' | 'name' | 'commissioningComplete'>;

export interface LinkDeviceDialogData {
  locationId: number | string;
  /** OID of the partner that owns the location — its unprovisioned devices are the candidates. */
  partnerOid: string;
  /** The Active installations at the location; when there are any, the user chooses where the device goes. */
  installations?: LinkTarget[];
  /** The installation the device joins, when the dialog is opened from that installation. */
  installationOid?: string;
  /** The provisioning statuses a device may have to be offered; only Linked when absent. */
  provisioning?: InstallationStatusEnum[];
}

export interface LinkDeviceResult {
  deviceId: string;
  /** The provisioning status the device had when it was chosen; Linked when absent. */
  provisioning?: InstallationStatusEnum;
  /** Null when the device is to get a new installation. */
  installationOid: string | null;
}

const NEW_INSTALLATION = 'new';

type DeviceRow = Record<string, unknown> & { deviceId: string };

/** Confirming resolves with the device and where it goes; cancelling resolves with undefined. */
@Component({
  selector: 'xemex-link-device-dialog',
  imports: [
    TranslateModule,
    DialogShellComponent,
    FilledButtonComponent,
    TextButtonComponent,
    TableComponent,
    TableFilterBarComponent,
    MatRadioModule,
    TagComponent,
  ],
  providers: [DeviceStore],
  templateUrl: './link-device-dialog.component.html',
  styleUrl: './link-device-dialog.component.scss',
})
export class LinkDeviceDialogComponent {
  private readonly dialogRef = inject(MatDialogRef<LinkDeviceDialogComponent>);
  protected readonly deviceStore = inject(DeviceStore);
  readonly data = inject<LinkDeviceDialogData>(MAT_DIALOG_DATA);

  private readonly provisioning = this.data.provisioning ?? [InstallationStatusEnum.Linked];

  // Registered and Linked devices share the list, so the user is shown which is which.
  protected readonly columns =
    this.provisioning.length > 1
      ? [
          { field: 'deviceLDN', label: 'devices.table.deviceLDN', width: '40%', bold: true },
          { field: 'deviceType', label: 'devices.table.type', width: '32%' },
          {
            field: 'deviceInstallationStatus',
            label: 'devices.table.installationStatus',
            width: '28%',
          },
        ]
      : [
          { field: 'deviceLDN', label: 'devices.table.deviceLDN', width: '40%', bold: true },
          { field: 'deviceType', label: 'devices.table.type', width: '32%' },
          { field: 'deviceStatus', label: 'devices.table.status', width: '28%' },
        ];

  protected readonly isLoading = computed(() => this.deviceStore.isLoading());
  protected readonly tableData = computed<DeviceRow[]>(
    () => (this.deviceStore.deviceData()?.devices ?? []) as unknown as DeviceRow[],
  );
  protected readonly totalCount = computed(() => this.deviceStore.deviceData()?.numberOfItems ?? 0);

  /** Single-selection: only the most recently clicked key is kept. */
  protected readonly selectedKeys = signal<Set<string | number>>(new Set());
  private selectedProvisioning = InstallationStatusEnum.Linked;
  protected readonly hasSelection = computed(() => this.selectedKeys().size > 0);

  protected readonly NEW_INSTALLATION = NEW_INSTALLATION;
  protected readonly targets = this.data.installationOid ? [] : (this.data.installations ?? []);
  protected readonly choice = signal<string | null>(null);
  protected readonly hasChoice = computed(
    () => this.targets.length === 0 || this.choice() !== null,
  );
  protected readonly canLink = computed(() => this.hasSelection() && this.hasChoice());

  protected readonly selectionKey = (row: Record<string, unknown>): string =>
    String(row['deviceId']);

  constructor() {
    this.deviceStore.getDevicesFilterMethod({
      partnerOid: this.data.partnerOid,
      provisioning: this.provisioning.join(','),
    });
  }

  protected onSearchChange(query: string): void {
    this.deviceStore.getDevicesFilterMethod({
      partnerOid: this.data.partnerOid,
      provisioning: this.provisioning.join(','),
      deviceLDN: query,
    });
  }

  protected onPageChange(event: PageEvent): void {
    this.deviceStore.getDevicesMethod(event.pageIndex);
  }

  protected onSelectedKeysChange(keys: Set<string | number>): void {
    const last = [...keys].pop();
    this.selectedKeys.set(last !== undefined ? new Set([last]) : new Set());
    // Read when the row is picked: a search or another page can drop it from the list later.
    const row = this.tableData().find(device => this.selectionKey(device) === String(last));
    if (row) {
      this.selectedProvisioning =
        String(row['deviceInstallationStatus']).toLowerCase() ===
        InstallationStatusEnum.Registered.toLowerCase()
          ? InstallationStatusEnum.Registered
          : InstallationStatusEnum.Linked;
    }
  }

  protected onChoiceChange(value: string): void {
    this.choice.set(value);
  }

  protected onCancel(): void {
    this.dialogRef.close();
  }

  protected onLink(): void {
    const [deviceId] = this.selectedKeys();
    if (deviceId === undefined || !this.hasChoice()) {
      return;
    }
    const choice = this.choice();
    const installationOid =
      this.data.installationOid ?? (choice === NEW_INSTALLATION ? null : choice);
    this.dialogRef.close({
      deviceId: String(deviceId),
      provisioning: this.selectedProvisioning,
      installationOid,
    } satisfies LinkDeviceResult);
  }
}
